import { computed, inject, Injectable, signal, WritableSignal } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable } from 'rxjs';
import { HttpTransportType, HubConnection, HubConnectionBuilder, LogLevel } from '@microsoft/signalr';
import { environment } from '../../../environments/environment';
import { SoundService } from '../sound/sound.service';
import { IdentityService } from '../identity/identity.service';
import { PortraitService } from '../portraits/portrait.service';
import { Router } from '@angular/router';
import { ToasterService } from '../toaster/toaster.service';
import { ChatService } from '../chat/chat.service';
import { AchievementService } from '../achievements/achievement.service';
import { Achievement, AchievementEventContext, AchievementUnlockedResult, UserAchievement } from '../../models/achievements.model';

@Injectable({
  providedIn: 'root',
})
export class MessengerService {
  private sound = inject(SoundService);
  private http = inject(HttpClient);
  private connection!: HubConnection;
  private identity = inject(IdentityService);
  private portraitService = inject(PortraitService);
  private router = inject(Router);
  public toaster = inject(ToasterService);
  private chatService = inject(ChatService);
  private achievementService = inject(AchievementService);


  private me = signal<any>(null);
  public externalUrl = signal<string | null>(null);

  currentUser = computed(() => {
    const me = this.me();
    let avatar = 'https://static.h5arena.com/images/avatars/Premium/180.jpg';

    if (me && this.portraitService.portraits().length > 0) {
      const portrait = this.portraitService.portraits().find(e => e.id == me.portraitId);
      if (portrait) {
        avatar = 'https://static.h5arena.com/images/avatars/' + portrait.fileName;
      }
    }

    console.log("computed avatar => ", avatar, me?.portraitId);

    return {
      userId: me ? me.userId : "",
      portraitId: me ? me.portraitId : "0",
      name: this.identity.getClaims()?.unique_name ?? "",
      avatar: avatar
    }
  });

  /*signal<any>({
    userId: "",
    portraitId: "",
    name: this.identity.getClaims().unique_name,
    avatar: 'https://static.h5arena.com/images/avatars/Premium/180.jpg'
  });*/

  friends: WritableSignal<any[]> = signal([]);
  conversations: WritableSignal<any[]> = signal([]);
  friendRequests: WritableSignal<any[]> = signal([]);
  activeConversationId: WritableSignal<string | null> = signal(null);
  readonly unlockedReward = this.achievementService.unlockedReward;
  readonly latestUnlockedAchievement = this.achievementService.latestUnlockedAchievement;
  readonly unlockedAchievementsList = this.achievementService.unlockedAchievementsList;

  public hasUnreadMessages = computed(() => {
    return this.conversations().some(c => c.hasUnread);
  });

  /**
   * Set current user profile.
   * @param userPortrait 
   */
  public setMe(userPortrait: any) {
    this.me.set(userPortrait);
  }

  constructor() {
    this.connection = new HubConnectionBuilder()
      .withUrl(environment.messengerUrl + "messengerHub", {
        transport: HttpTransportType.WebSockets,
        accessTokenFactory: () => {
          return JSON.parse(sessionStorage.getItem("token")!).token;
        }
      })
      .configureLogging(LogLevel.Information)
      .withAutomaticReconnect()
      .build();

    // Wire up subscriptions to hub methods.
    this.hubSubscriptions();

    // Set current user.
    this.portraitService.getMine().subscribe(userPortrait => {
      this.me.set(userPortrait);
    });
  }

  /**
   * Connect to messenger.
   * @returns 
   */
  connect() {
    this.friends.set([]);
    return this.connection.start().then(() => {
      console.log("Started signalr connection.");
    }).catch(error => {
      this.toaster.danger("Failed to connect to messenger server.");
      throw error;
    });
  }

  /**
   * Disconnect from messenger.
   */
  disconnect() {
    this.friends.set([]);
    this.connection.stop();
  }

  /**
   * Accept a friend request.
   * @param request 
   */
  acceptFriendRequest(request: any) {
    try {
      this.connection.invoke("acceptFriendship", request);
    }
    catch (error) {
      console.error(error);
    }
  }

  /**
   * Decline a friend request.
   * @param request 
   */
  declineFriendRequest(request: any) {
    try {
      this.connection.invoke("declineFriendship", request);
    }
    catch (error) {
      console.error(error);
    }
  }

  /**
   * Removes a friend.
   * @param friend 
   */
  removeFriend(friend: any) {
    try {
      const request = {
        userId: this.currentUser().userId,
        friendUserId: friend.id || friend.userId || friend.guid,
        groupName: "Friends"
      };
      this.connection.invoke("removeFriendship", request);
    }
    catch (error) {
      console.error(error);
    }
  }

  /**
   * Send a friend request.
   * @param request 
   */
  sendFriendRequest(request: any) {
    try {
      console.log("Sending friend request => ", request);
      this.connection.invoke("requestFriendship", request);
    }
    catch (error) {
      console.error(error);
    }
  }

  /**
   * Send a direct message to a user.
   * @param message 
   */
  sendMessage(message: any) {
    try {
      this.connection.invoke("senddirectmessage", message);
    }
    catch (error) {
      console.error(error);
    }
  }

  /**
   * Send an arena invite.
   * @param directMessage 
   * @param destination 
   * @param password 
   */
  sendInvite(directMessage: any, destination: string, password?: string) {
    try {
      this.connection.invoke("sendInvite", directMessage, destination, password ?? null);
    }
    catch (error) {
      console.error(error);
    }
  }

  /**
   * Triggers an update of the user.
   */
  updateUser() {
    try {
      this.connection.invoke("updateUser");
    }
    catch (error) {
      console.error(error);
    }
  }

  /**
   * Monitor hub method subscriptions.
   */
  private hubSubscriptions() {
    this.connection.onreconnecting((error) => {
      this.toaster.warning("Reconnecting to messenger server...");
    });

    this.connection.onreconnected((connectionId) => {
      this.toaster.success("Reconnected to messenger server.");
    });

    this.connection.onclose((error) => {
      if (error) {
        this.toaster.danger("Connection to messenger server lost.");
      }
    });

    this.connection.on('notifications', requests => {
      console.log("Notifications => ", requests);
      // Updates friend requests.
      this.friendRequests.set(requests);
    });

    const getFriendId = (f: any): string | null => {
      if (!f) return null;
      return f.userId || f.id || f.UserId || f.Id || null;
    };

    const isFriendMatch = (f1: any, f2: any): boolean => {
      if (!f1 || !f2) return false;
      const id1 = getFriendId(f1);
      const id2 = getFriendId(f2);
      if (id1 && id2) {
        return id1 === id2;
      }
      if (f1.userName && f2.userName) {
        return f1.userName.toLowerCase() === f2.userName.toLowerCase();
      }
      return false;
    };

    this.connection.on('friendRemoved', (payload: any) => {
      console.log("Friend removed payload => ", payload);
      const targetId = payload?.friendUserId === this.currentUser()?.userId ? payload?.userId : (payload?.friendUserId || payload?.userId);
      if (targetId) {
        this.friends.update(friends => friends.filter(f => !isFriendMatch(f, { userId: targetId })));
        this.conversations.update(convs => convs.filter(c => !isFriendMatch(c.friend, { userId: targetId })));
      }
    });

    this.connection.on('friends', (data: any[]) => {
      if (!Array.isArray(data)) return;

      const currentTimestamp = new Date(Date.now());
      const isInitialLoad = this.friends().length === 0;

      if (data.length > 1 || (isInitialLoad && data.length <= 1)) {
        // Set the timestamp.
        data.forEach((friend: any) => {
          friend.timestamp = currentTimestamp.toString();
        });

        // Full friends list (initial log on).
        this.friends.set(data);

        // Update initial status of friends in conversations list.
        this.conversations.update(convs => {
          return convs.map(c => {
            const matchingFriend = data.find((f: any) => isFriendMatch(f, c.friend));
            if (matchingFriend) {
              return {
                ...c,
                friend: {
                  ...c.friend,
                  ...matchingFriend
                }
              };
            }
            return c;
          });
        });
      } else if (data.length === 1) {
        const updatedFriend = { ...data[0], timestamp: currentTimestamp.toString() };

        const currentFriends = this.friends();
        const previousFriend = currentFriends.find(f => isFriendMatch(f, updatedFriend));

        if (previousFriend) {
          const wasOffline = previousFriend.status === 0 || previousFriend.status === undefined || previousFriend.status === null;
          const isOffline = updatedFriend.status === 0 || updatedFriend.status === undefined || updatedFriend.status === null;

          if (wasOffline && updatedFriend.status === 1) {
            this.toaster.success(`${updatedFriend.userName || previousFriend.userName} has signed on`);
          } else if (!wasOffline && isOffline) {
            this.toaster.danger(`${updatedFriend.userName || previousFriend.userName} has signed off`);

            // Append system message to the conversation
            const systemMessage = {
              isSystemMessage: true,
              text: `${updatedFriend.userName || previousFriend.userName} has signed off.`,
              timestamp: currentTimestamp.toString()
            };
            this.conversations.update(convs =>
              convs.map(c => {
                if (isFriendMatch(c.friend, updatedFriend)) {
                  return {
                    ...c,
                    messages: [...(c.messages || []), systemMessage]
                  };
                }
                return c;
              })
            );
          }
        } else {
          if (updatedFriend.status === 1 && updatedFriend.userName) {
            this.toaster.success(`${updatedFriend.userName} has signed on`);
          }
        }

        // Update our friends list (replace existing or append new if not existing).
        this.friends.update(friends => {
          const exists = friends.some(f => isFriendMatch(f, updatedFriend));
          if (exists) {
            return friends.map(f => isFriendMatch(f, updatedFriend) ? { ...f, ...updatedFriend } : f);
          }

          // Add new friend.
          return [...friends, updatedFriend];
        });

        // Update our conversations list.
        this.conversations.update(convs => {
          return convs.map(c => {
            if (isFriendMatch(c.friend, updatedFriend)) {
              return {
                ...c,
                friend: {
                  ...c.friend,
                  ...updatedFriend
                }
              };
            }
            return c;
          });
        });
      }
    });

    this.connection.on('directmessage', (dm: any) => {
      console.log("Incoming DM => ", dm);
      dm.classes = "font-" + dm.color;

      // Add Timestamp.
      const now = new Date();
      const dateString = now.toString();
      dm.timestamp = now.toString();

      // Linkify detection
      const urlRegex = /(https?:\/\/[^\s]+)/g;
      const match = dm.text.match(urlRegex);
      if (match) {
        const url = match[0];
        this.http.post(environment.platformUrl + 'api/chat/linkify', {
          url: url,
          title: "",
          description: "",
          image: ""
        }).subscribe({
          next: (res: any) => {
            if (res.image && typeof res.image === 'string') {
              res.image = [`data:image/png;base64,${res.image}`];
            }
            dm.linkPreview = res;
            this.conversations.set([...this.conversations()]);
          },
          error: (err) => console.error("Linkify failed", err)
        });
      }

      const myUserId = this.currentUser()?.userId;
      const isFromMe = myUserId ? (dm.fromUserId === myUserId) : dm.isMe;
      const partnerId = isFromMe ? dm.toUserId : dm.fromUserId;
      const partnerUser = isFromMe ? dm.toUser : dm.fromUser;

      const pagerEl = document.getElementById('pagerOffcanvas');
      const isPagerOpen = pagerEl ? pagerEl.classList.contains('show') : false;
      const isViewingConversation = isPagerOpen && this.activeConversationId() === partnerId;
      const hasUnread = !isViewingConversation;

      if (!isFromMe && hasUnread) {
        this.sound.playDirectMessage();

        // Notify the Electron window when a DM is received.
        if (this.isElectron()) {
          (window as any).windowControls.notify();
        }

        if (document.hidden) {
          let title = `New message from ${partnerUser?.userName || 'Someone'}`;
          let body = dm.text;
          let iconUrl = '/images/logo_1_4.png';

          if (partnerUser?.portraitId) {
            const portrait = this.portraitService.portraits().find(e => e.id == partnerUser.portraitId);
            if (portrait) {
              iconUrl = `https://static.h5arena.com/images/avatars/${portrait.fileName}`;
            }
          }

          this.toaster.desktopNotification(title, body, iconUrl, () => {
            const bootstrap = (window as any).bootstrap;
            if (bootstrap && bootstrap.Offcanvas) {
              const el = document.getElementById('pagerOffcanvas');
              if (el) {
                const bsOffcanvas = bootstrap.Offcanvas.getInstance(el) || new bootstrap.Offcanvas(el);
                bsOffcanvas.show();
              }
            }
            this.activeConversationId.set(partnerId);
          });
        }
      }

      let existing = this.conversations().find(e => e.friendUserId === partnerId);
      if (existing) {
        existing.messages.push(dm);
        existing.hasUnread = hasUnread;

        // Update signal
        this.conversations.update(items =>
          items.map(item =>
            item.friendUserId === partnerId
              ? { ...item, ...existing } : item
          )
        );
      } else {
        let conversation = {
          friendUserId: partnerId,
          friend: partnerUser || this.friends().find(f => (f.id || f.userId) === partnerId),
          messages: [] as any[],
          hasUnread: hasUnread
        };

        // Push the message into the conversation.
        conversation.messages.push(dm);

        // Update the signal.
        this.conversations.set([...this.conversations(), conversation]);
      }
    });

    this.connection.on("requestFriendship", (request: any) => {
      console.log("Friend request => ", request);
      const oldNotifications = this.friendRequests();
      this.friendRequests.set([...oldNotifications, request]);
    });

    this.connection.on("invite", (message: any, destination: string, password?: string) => {
      console.log("Received invite => ", message, destination, password);

      this.sound.playChime();

      const text = message && typeof message === 'object' ? message.text : message;
      const fromUserName = message?.fromUser?.userName || "Someone";
      const notificationId = Math.random().toString(36).substring(2, 9);

      let toastId = "";

      toastId = this.toaster.invite(
        text,
        () => {
          // Accept logic
          if (this.chatService.isConnected()) {
            this.chatService.join(destination, password ?? "");
          } else {
            this.chatService.connect(destination, password ?? "");
          }
          this.router.navigate(['/home/chat', destination]);
          this.friendRequests.update(current => current.filter(n => n.id !== notificationId));
        },
        () => {
          console.log("Invite declined by user.");
          this.friendRequests.update(current => current.filter(n => n.id !== notificationId));
        }
      );

      // Create notification object
      const inviteNotification = {
        id: notificationId,
        toastId: toastId,
        isInvite: true,
        fromUserName: fromUserName,
        destination: destination,
        password: password,
        text: text
      };

      this.friendRequests.update(current => [...current, inviteNotification]);
    });

    this.connection.on('notonline', (userId: string) => {
      console.log("User not online => ", userId);
      const systemMessage = {
        isSystemMessage: true,
        text: "The previous message was not sent because the user is not online at this time.",
        timestamp: new Date().toString()
      };

      let existing = this.conversations().find(c => c.friendUserId === userId);
      if (existing) {
        existing.messages.push(systemMessage);

        this.conversations.update(items =>
          items.map(item =>
            item.friendUserId === userId
              ? { ...item, ...existing } : item
          )
        );
      }
    });

    this.connection.on('systemmessage', (text: string) => {
      console.log("System message received => ", text);
      const activeId = this.activeConversationId();
      if (!activeId) return;

      const systemMessage = {
        isSystemMessage: true,
        text: text,
        timestamp: new Date().toString()
      };

      let existing = this.conversations().find(c => c.friendUserId === activeId);
      if (existing) {
        existing.messages.push(systemMessage);

        this.conversations.update(items =>
          items.map(item =>
            item.friendUserId === activeId
              ? { ...item, ...existing } : item
          )
        );
      }
    });

    this.connection.on("recvAward", (award: any) => {
      this.achievementService.handleAward(award);
    });

    this.connection.on("AchievementUnlocked", (reward: AchievementUnlockedResult) => {
      this.achievementService.handleAchievementUnlocked(reward);
    });


  }

  /**
   * Mark a conversation as read.
   * @param friendUserId 
   */
  public markAsRead(friendUserId: string) {
    this.conversations.update(items =>
      items.map(item =>
        item.friendUserId === friendUserId
          ? { ...item, hasUnread: false } : item
      )
    );
  }

  /**
   * Close and remove a conversation.
   * @param friendUserId 
   */
  public closeConversation(friendUserId: string) {
    this.conversations.update(items => items.filter(item => item.friendUserId !== friendUserId));
    if (this.activeConversationId() === friendUserId) {
      this.activeConversationId.set(null);
    }
  }

  private isElectron(): boolean {
    return navigator.userAgent.toLowerCase().includes('electron');
  }

  /**
   * Fetch all active achievement definitions from backend.
   */
  public getAchievements(): Observable<Achievement[]> {
    return this.achievementService.getAchievements();
  }

  /**
   * Fetch achievement progress for a user.
   * @param userId Optional user ID; defaults to current authenticated user ID.
   */
  public getUserAchievements(userId?: string): Observable<UserAchievement[]> {
    return this.achievementService.getUserAchievements(userId || this.currentUser()?.userId);
  }

  /**
   * Perform daily check-in to trigger login achievement evaluation.
   */
  public dailyCheckin(): Observable<any> {
    return this.achievementService.dailyCheckin();
  }

  /**
   * Evaluate a custom achievement event.
   * @param context Achievement event context.
   */
  public evaluateAchievementEvent(context: AchievementEventContext): Observable<any> {
    return this.achievementService.evaluateAchievementEvent(context);
  }

}

